import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { basePageWrap } from '../BasePage';
import styles from './SoundArchivePage.module.scss';
import {
    useProductSelection,
    setGetArguments,
    getSelectedProductIdFromCookie,
} from '../../utils/singleProductChooser';

// Components
import Icon from '../../components/Icon';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import TabLinksVertical from '../../components/TabLinksVertical';

import Image from '../../components/Image';
import YouTubeVideo from '../../components/YouTubeVideo/YouTubeVideo';
import PlayableAudioCardList from '../../components/PlayableAudioCardList/PlayableAudioCardList';
import SingleChooser from '../../components/SingleChooser';
import Pagination from '../../components/Pagination';
import Breadcrumbs from '../../components/Breadcrumbs';
import Chip from '../../components/Chip';
import Button from '../../components/Button';
import { AccordionContext } from '../../contexts/AccordionContext';

export { AccordionContext };

const SoundArchivePage = ({
    settings = {
        header: {},
        footer: {},
    },
    title = '',
    preamble,
    videoUrl = '',
    image = {},
    breadcrumbs = {},
    categories = null,
    selectText,
    tabLinksData = { primary: [], secondary: [] },
    selectedProductId = null,
    selectedSortOrder,
    sortOrderSelectionOptions,
    productSelectionOptions = {
        legacyProducts: [],
        activeProducts: [],
    },
    items = null,
    row = false,
    hasTags,
    pagination = null,
    selectedCategoryFilter = null,
    selectFieldData,
    selectedProductLatestSoundVersion,
    categoriesFilterSelectionOptions,
    hideTitle = false,
    //showSearch,
}) => {
    const [isExpandedAll, setIsExpandedAll] = useState(false);
    const router = useRouter();
    const { selectedProduct, setProductById } = useProductSelection({
        ...productSelectionOptions,
        selectedProductId,
    });

    const [showMobileFilters, setShowMobileFilters] = useState(false);

    useEffect(() => {
        if (selectedCategoryFilter !== null) {
            setIsExpandedAll(true);
        } else {
            setIsExpandedAll(false);
        }
    }, [selectedCategoryFilter]);

    const setQueryArgument = (url, value, paramName) => {
        const _url = new URL(url, 'https://dummy.url');
        if (value) {
            _url.searchParams.set(paramName, value);
        } else {
            _url.searchParams.delete(paramName);
        }
        return _url.pathname + _url.search;
    };

    const handlePaginationOnChange = (e) => {
        router.replace(
            setQueryArgument(window.location, e, 'page'),
            undefined,
            { scroll: true }
        );
    };

    const handleSortOrderOnChange = (e) => {
        const baseUrl = setQueryArgument(window.location, 1, 'page');
        router.replace(setQueryArgument(baseUrl, e, 'sort'), undefined, {
            scroll: false,
        });
    };

    const handleCategoryOnChange = (categoryId) => {
        if (selectedCategoryFilter?.value === categoryId) {
            categoryId = 0;
        }
        const baseUrl = setQueryArgument(window.location, 1, 'page');
        router.replace(
            setQueryArgument(baseUrl, categoryId, 'category'),
            undefined,
            {
                scroll: false,
            }
        );
    };

    useEffect(() => {
        let baseUrl = window.location;
        const shouldResetPaginationQuery =
            router.query &&
            router.query.page !== undefined &&
            router.query.page !== '1';

        baseUrl = setQueryArgument(baseUrl, 0, 'category');
        if (shouldResetPaginationQuery) {
            baseUrl = setQueryArgument(baseUrl, 1, 'page');
        }

        const cookie = getSelectedProductIdFromCookie();

        const transformedCookieValue =
            typeof cookie !== 'undefined' ? cookie : 0;

        const transformedSelectedProductValue =
            typeof selectedProduct !== 'undefined'
                ? selectedProduct
                    ? selectedProduct.value
                    : 0
                : 0;

        const hasProductFilterQuery =
            router.query &&
            router.query.selected_product &&
            router.query.selected_product.length > 0;

        if (
            transformedSelectedProductValue == transformedCookieValue &&
            (transformedCookieValue !== 0 || hasProductFilterQuery)
        ) {
            router.replace(
                setGetArguments(
                    baseUrl,
                    selectedProduct ? selectedProduct.value : 0
                ),
                undefined,
                { scroll: false }
            );
        }
    }, [selectedProduct]);

    const path = router.query !== undefined && router.query.path[1];

    useEffect(() => {
        let baseUrl = window.location;

        if (items && items.length === 0) {
            router.replace(setGetArguments(baseUrl, 0), undefined, {
                scroll: false,
            });
            setProductById(0);
        }
    }, [path]);

    useEffect(() => {
        const cookieValue = getSelectedProductIdFromCookie();
        if (cookieValue) {
            setProductById(cookieValue);
        }
    }, []);

    const handleClearSelection = () => {
        setProductById(0);
    };

    const tabLinks = {
        ...tabLinksData,
        primary: tabLinksData.primary.map((x) => ({
            ...x,
            href: setGetArguments(
                x.href,
                selectedProduct ? selectedProduct.value : null
            ),
        })),
    };

    const selectedProductLatestSoundVersionNumber =
        selectedProductLatestSoundVersion
            ? selectedProductLatestSoundVersion.match(/\d+$/)[0]
            : null;

    return (
        <AccordionContext.Provider value={{ isExpandedAll, setIsExpandedAll }}>
            <div className={styles['SoundArchivePage']}>
                <Header {...settings.header} />
                <main>
                    <div className={styles['SoundArchivePage__Container']}>
                        <div
                            className={styles['SoundArchivePage__Breadcrumbs']}>
                            <Breadcrumbs {...breadcrumbs} />
                        </div>
                        <div
                            className={
                                styles['SoundArchivePage__ContentContainer']
                            }>
                            <div
                                className={styles['SoundArchivePage__Desktop']}>
                                {tabLinks && <TabLinksVertical {...tabLinks} />}
                            </div>
                            <div
                                className={styles['SoundArchivePage__Content']}>
                                <div
                                    className={
                                        styles['SoundArchivePage__Hero']
                                    }>
                                    <div
                                        className={
                                            styles[
                                                'SoundArchivePage__InfoWrapper'
                                            ]
                                        }>
                                        {!hideTitle && (
                                            <h1
                                                className={
                                                    styles[
                                                        'SoundArchivePage__Title'
                                                    ]
                                                }>
                                                {title}
                                            </h1>
                                        )}

                                        {preamble && (
                                            <p
                                                className={
                                                    styles[
                                                        'SoundArchivePage__Preamble'
                                                    ]
                                                }>
                                                {preamble}
                                            </p>
                                        )}
                                    </div>
                                    {videoUrl && (
                                        <div
                                            className={
                                                styles[
                                                    'SoundArchivePage__ImageWrapper'
                                                ]
                                            }>
                                            <YouTubeVideo videoId={videoUrl} />
                                        </div>
                                    )}
                                    {!videoUrl && image.url && (
                                        <div
                                            className={
                                                styles[
                                                    'SoundArchivePage__ImageWrapper'
                                                ]
                                            }>
                                            <Image
                                                {...image}
                                                layout="fill"
                                                objectFit="cover"
                                                priority={true}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={
                                        styles['SoundArchivePage__Mobile']
                                    }>
                                    {tabLinks && (
                                        <TabLinksVertical {...tabLinks} />
                                    )}
                                </div>
                                <div
                                    className={
                                        styles[
                                            'SoundArchivePage__SelectWrapper'
                                        ]
                                    }>
                                    <p
                                        className={
                                            styles[
                                                'SoundArchivePage__SelectText'
                                            ]
                                        }>
                                        Select Product to browse compatible
                                        content for your Nord Instrument.
                                    </p>{' '}
                                    <div
                                        className={
                                            styles[
                                                'SoundArchivePage__SelectField'
                                            ]
                                        }>
                                        <div
                                            className={
                                                styles[
                                                    'SoundArchivePage__SelectFieldWrapper'
                                                ]
                                            }>
                                            <SingleChooser
                                                modifiers={['alternative']}
                                                placeholder={
                                                    selectedProduct
                                                        ? selectedProduct.label
                                                        : 'Select product'
                                                }
                                                onChange={(e) =>
                                                    setProductById(e)
                                                }
                                                tooltip={
                                                    'Choose your product to see compatible sounds'
                                                }
                                                showClearButton={true}
                                                onClick={handleClearSelection}
                                                options={
                                                    productSelectionOptions.activeProducts
                                                }
                                                secondaryOptions={
                                                    productSelectionOptions.legacyProducts
                                                }
                                                secondaryOptionName={
                                                    'Legacy products'
                                                }
                                                activeValue={
                                                    selectedProduct
                                                        ? selectedProduct.value
                                                        : null
                                                }
                                            />
                                        </div>
                                        <span
                                            className={
                                                styles[
                                                    'SoundArchivePage__VersionText'
                                                ]
                                            }>
                                            {selectedProductLatestSoundVersion}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    className={classNames(
                                        styles[
                                            'SoundArchivePage__FilterButton'
                                        ],
                                        {
                                            [styles[
                                                'SoundArchivePage__FilterButton--Expanded'
                                            ]]: showMobileFilters,
                                        }
                                    )}
                                    aria-expanded={showMobileFilters}
                                    aria-controls="filters-wrapper"
                                    onClick={() =>
                                        setShowMobileFilters(!showMobileFilters)
                                    }>
                                    {showMobileFilters
                                        ? 'Hide filters'
                                        : 'Show filters'}{' '}
                                    <Icon type="filter" />
                                </button>

                                <div
                                    id="filters-wrapper"
                                    className={classNames(
                                        styles['SoundArchivePage__Filters'],
                                        {
                                            [styles[
                                                'SoundArchivePage__Filters--Expanded'
                                            ]]: showMobileFilters,
                                        }
                                    )}>
                                    <div
                                        className={
                                            styles[
                                                'SoundArchivePage__OptionsColumn'
                                            ]
                                        }>
                                        {productSelectionOptions && (
                                            <div
                                                className={
                                                    styles[
                                                        'SoundArchivePage__OptionsItem'
                                                    ]
                                                }>
                                                <SingleChooser
                                                    modifiers={['wide']}
                                                    placeholder={
                                                        selectedProduct
                                                            ? selectedProduct.label
                                                            : 'Select product'
                                                    }
                                                    onChange={setProductById}
                                                    tooltip={
                                                        'Choose your product to see compatible sounds'
                                                    }
                                                    options={
                                                        productSelectionOptions.activeProducts
                                                    }
                                                    secondaryOptions={
                                                        productSelectionOptions.legacyProducts
                                                    }
                                                    secondaryOptionName={
                                                        'Legacy products'
                                                    }
                                                    activeValue={
                                                        selectedProduct
                                                            ? selectedProduct.value
                                                            : null
                                                    }
                                                    showClearButton={true}
                                                    onClick={
                                                        handleClearSelection
                                                    }
                                                />
                                            </div>
                                        )}

                                        {categoriesFilterSelectionOptions &&
                                            categoriesFilterSelectionOptions.length >
                                                0 && (
                                                <div
                                                    className={
                                                        styles[
                                                            'SoundArchivePage__OptionsItem'
                                                        ]
                                                    }>
                                                    <SingleChooser
                                                        modifiers={['wide']}
                                                        placeholder={
                                                            selectedCategoryFilter
                                                                ? selectedCategoryFilter.label
                                                                : 'Category'
                                                        }
                                                        onChange={
                                                            handleCategoryOnChange
                                                        }
                                                        options={
                                                            categoriesFilterSelectionOptions
                                                        }
                                                        activeValue={
                                                            selectedCategoryFilter
                                                                ? selectedCategoryFilter.value
                                                                : null
                                                        }
                                                        hideClearButton={true}
                                                    />
                                                </div>
                                            )}
                                        {sortOrderSelectionOptions &&
                                            sortOrderSelectionOptions.length >
                                                0 && (
                                                <div
                                                    className={
                                                        styles[
                                                            'SoundArchivePage__OptionsItem'
                                                        ]
                                                    }>
                                                    <SingleChooser
                                                        modifiers={['wide']}
                                                        placeholder={
                                                            selectedSortOrder
                                                                ? selectedSortOrder.label
                                                                : sortOrderSelectionOptions[0]
                                                                      .label
                                                        }
                                                        onChange={
                                                            handleSortOrderOnChange
                                                        }
                                                        options={
                                                            sortOrderSelectionOptions
                                                        }
                                                        activeValue={
                                                            selectedSortOrder
                                                                ? selectedSortOrder.value
                                                                : null
                                                        }
                                                        hideClearButton={true}
                                                        alignMenuRight={true}
                                                        alignRight={true}
                                                    />
                                                </div>
                                            )}
                                    </div>
                                </div>
                                {categoriesFilterSelectionOptions &&
                                    categoriesFilterSelectionOptions.length >
                                        0 && (
                                        <div
                                            className={
                                                styles[
                                                    'SoundArchivePage__Chips'
                                                ]
                                            }>
                                            <Chip
                                                title={'All'}
                                                id={0}
                                                selected={
                                                    !selectedCategoryFilter
                                                }
                                                onClick={() =>
                                                    handleCategoryOnChange(0)
                                                }
                                            />
                                            {categoriesFilterSelectionOptions.map(
                                                (category, index) => (
                                                    <Chip
                                                        id={category.value}
                                                        key={`chip-${category.value}`}
                                                        title={category.label}
                                                        selected={
                                                            selectedCategoryFilter &&
                                                            selectedCategoryFilter.value ===
                                                                category.value
                                                        }
                                                        onClick={() =>
                                                            handleCategoryOnChange(
                                                                category.value
                                                            )
                                                        }
                                                    />
                                                )
                                            )}
                                        </div>
                                    )}

                                <div
                                    className={
                                        styles['SoundArchivePage__List']
                                    }>
                                    {sortOrderSelectionOptions &&
                                        sortOrderSelectionOptions.length >
                                            0 && (
                                            <div
                                                className={
                                                    styles[
                                                        'SoundArchivePage__Option'
                                                    ]
                                                }>
                                                <SingleChooser
                                                    modifiers={['wide']}
                                                    placeholder={
                                                        selectedSortOrder
                                                            ? selectedSortOrder.label
                                                            : sortOrderSelectionOptions[0]
                                                                  .label
                                                    }
                                                    onChange={
                                                        handleSortOrderOnChange
                                                    }
                                                    options={
                                                        sortOrderSelectionOptions
                                                    }
                                                    activeValue={
                                                        selectedSortOrder
                                                            ? selectedSortOrder.value
                                                            : null
                                                    }
                                                    hideClearButton={true}
                                                    alignMenuRight={true}
                                                    alignRight={true}
                                                />
                                            </div>
                                        )}

                                    {categories && categories.length ? (
                                        categories.map((category, index) => (
                                            <PlayableAudioCardList
                                                containImages={
                                                    category.containImages
                                                }
                                                hasTags={category.hasTags}
                                                key={`cardlist-${category.title}-${index}`}
                                                row={category.row}
                                                header={category.title}
                                                items={category.cards}
                                                triggerProductChooserOnDownload={
                                                    true
                                                }
                                                isHighlight={true}
                                                selectedProductId={
                                                    selectedProduct
                                                        ? selectedProduct.value
                                                        : null
                                                }
                                                productSelectionOptions={
                                                    productSelectionOptions
                                                }
                                                selectedProductLatestSoundVersionNumber={
                                                    selectedProductLatestSoundVersionNumber
                                                }
                                            />
                                        ))
                                    ) : items && items.length ? (
                                        <>
                                            <PlayableAudioCardList
                                                row={row}
                                                hasTags={hasTags}
                                                key={'cardlist'}
                                                items={items}
                                                isHighlight={false}
                                                selectedProductId={
                                                    selectedProduct
                                                        ? selectedProduct.value
                                                        : null
                                                }
                                                defaultAccordionSortOrder={
                                                    selectedSortOrder
                                                        ? selectedSortOrder.value
                                                        : sortOrderSelectionOptions[0]
                                                              .label
                                                }
                                                productSelectionOptions={
                                                    productSelectionOptions
                                                }
                                                selectedProductLatestSoundVersionNumber={
                                                    selectedProductLatestSoundVersionNumber
                                                }
                                            />

                                            {pagination &&
                                                pagination.totalPages &&
                                                pagination.totalPages > 1 && (
                                                    <div
                                                        className={
                                                            styles[
                                                                'SoundArchivePage__Pagination'
                                                            ]
                                                        }>
                                                        <Pagination
                                                            key={
                                                                'cardlist-paginated'
                                                            }
                                                            totalPages={
                                                                pagination.totalPages
                                                            }
                                                            currentPage={
                                                                pagination.currentPage
                                                            }
                                                            itemsPerPage={12}
                                                            onPageChange={
                                                                handlePaginationOnChange
                                                            }
                                                        />
                                                    </div>
                                                )}
                                        </>
                                    ) : (
                                        <div
                                            className={
                                                styles[
                                                    'SoundArchivePage__NoResult'
                                                ]
                                            }>
                                            <p>
                                                <b>
                                                    {selectedProduct &&
                                                        selectedProduct.label}
                                                </b>{' '}
                                                does not have any available
                                                content in the selected library.
                                                <br />
                                                <br />
                                                However, you can try exploring
                                                other <b>Libraries</b>,{' '}
                                                <b>Collections</b>, or{' '}
                                                <b>Signature Sound Banks</b> to
                                                find what you're looking for.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
                <Footer {...settings.footer} />
            </div>
        </AccordionContext.Provider>
    );
};

const SearchField = ({ handleSubmit }) => {
    const [query, setQuery] = useState(false);

    return (
        <form
            className={styles['SoundArchivePage__Search']}
            onSubmit={(e) => handleSubmit(e, query)}>
            <input
                placeholder={'Search'}
                className={styles['SoundArchivePage__SearchField']}
                type="search"
                onChange={(e) => {
                    setQuery(e.target.value);
                }}
            />
            <button
                type="submit"
                className={styles['SoundArchivePage__SearchButton']}>
                <Icon type="searchSmall" />
            </button>
        </form>
    );
};

const ActiveFilter = ({ label, onRemoveFilter }) => {
    return (
        <div className={styles['SoundArchivePage__ActiveFilter']}>
            <span className={styles['SoundArchivePage__ActiveFilterLabel']}>
                {label}
            </span>
            <button
                onClick={onRemoveFilter}
                className={styles['SoundArchivePage__ActiveFilterButton']}>
                <Icon type="crossSmall" />
                <span className="sr-only">Remove {label}</span>
            </button>
        </div>
    );
};

SoundArchivePage.propTypes = {
    categories: PropTypes.array,
    settings: PropTypes.object,
    title: PropTypes.string,
    preamble: PropTypes.string,
    image: PropTypes.object,
    videoUrl: PropTypes.string,
    selectedProductId: PropTypes.number,
    productSelectionOptions: PropTypes.shape({
        legacyProducts: PropTypes.array,
        activeProducts: PropTypes.array,
    }),
    tabLinksData: PropTypes.shape({
        primary: PropTypes.array,
        secondary: PropTypes.array,
    }),
    items: PropTypes.array,
    pagination: PropTypes.shape({
        currentPage: PropTypes.number,
        total_pages: PropTypes.number,
    }),
    hideTitle: PropTypes.bool,
};

export default basePageWrap(SoundArchivePage);
